import type {
  ActorDatabaseHandle,
  ActorTransaction,
} from "@-/dev.improve.haniverse.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { eq } from "@-/com.npmjs.drizzle-orm";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { REVIEW_RATING } from "@-/dev.improve.haniverse.lib.cards.user-card.fsrs";

/**
 * Review recharge: charge built up by reviewing and spent on new characters. A full bar lets the
 * learner switch New Cards on; the first new character it brings drains the whole bar and opens its
 * unit.
 */
export const REVIEW_RECHARGE_FULL = 100;

/** Review recharge points a local day's reviews must earn to keep the daily streak. */
export const STREAK_DAILY_RECHARGE_POINTS = 100;

/** Points one committed review earns: a lapse (Again) barely moves the bar; a recall moves it 5. */
export function reviewRechargePoints(rating: number): number {
  return rating === REVIEW_RATING.Again ? 1 : 5;
}

type ActorReader = ActorDatabaseHandle | ActorTransaction;

type ReviewRechargeEvent = {
  at: string;
  kind: "introduction" | "review";
  progressAfter: number;
};

/** The later of two events; a review wins a tie, since an introduction never shares its instant. */
function laterEvent(
  review: ReviewRechargeEvent | null,
  introduction: ReviewRechargeEvent | null,
): ReviewRechargeEvent | null {
  if (review === null || introduction === null) {
    return review ?? introduction;
  }
  return introduction.at > review.at ? introduction : review;
}

/**
 * The latest stored balance event matching `progressAfter`. Reviews in one sentence batch share
 * their `reviewedAt`; reviews only ever raise the balance, so among ties the highest is the last.
 */
async function readLatestEvent(
  db: ActorReader,
  progressAfter: { isNotNull: true } | { lt: number },
): Promise<ReviewRechargeEvent | null> {
  const [review, introduction] = await Promise.all([
    db.query.userCardReview.findFirst({
      columns: { reviewedAt: true, reviewRechargeAfter: true },
      // oxlint-disable-next-line perfectionist/sort-objects -- key order is sort priority: time first, then the balance among a batch's tied timestamps.
      orderBy: { reviewedAt: "desc", reviewRechargeAfter: "desc" },
      where: { reviewRechargeAfter: progressAfter },
    }),
    db.query.userCard.findFirst({
      columns: { reviewRechargeAfter: true, startedStudyingAt: true },
      orderBy: { startedStudyingAt: "desc" },
      where: { reviewRechargeAfter: progressAfter, startedStudyingAt: { isNotNull: true } },
    }),
  ]);
  return laterEvent(
    review?.reviewRechargeAfter == null
      ? null
      : { at: review.reviewedAt, kind: "review", progressAfter: review.reviewRechargeAfter },
    introduction?.reviewRechargeAfter == null || introduction.startedStudyingAt == null
      ? null
      : {
          at: introduction.startedStudyingAt,
          kind: "introduction",
          progressAfter: introduction.reviewRechargeAfter,
        },
  );
}

/**
 * The balance before any event was stored: review points since the latest character
 * introduction, as the bar was computed before review recharge became a stored balance.
 */
async function readLegacyReviewRecharge(db: ActorReader): Promise<number> {
  const lastLesson = await db.query.userCard.findFirst({
    columns: { startedStudyingAt: true },
    orderBy: { startedStudyingAt: "desc" },
    where: {
      startedStudyingAt: { isNotNull: true },
      userGraphemeMeaningCard: { grapheme: { isNotNull: true } },
    },
  });
  const reviews = await db.query.userCardReview.findMany({
    columns: { id: true },
    // Every review earns at least one point, so the bar is full within this many rows.
    limit: REVIEW_RECHARGE_FULL,
    where: {
      reviewRechargeAfter: { isNull: true },
      ...(lastLesson?.startedStudyingAt == null
        ? {}
        : { reviewedAt: { gt: lastLesson.startedStudyingAt } }),
    },
    with: { fsrsCardReviewLog: { columns: { rating: true } } },
  });
  const points = reviews.reduce(
    (sum, review) =>
      sum +
      (review.fsrsCardReviewLog == null
        ? 0
        : reviewRechargePoints(review.fsrsCardReviewLog.rating)),
    0,
  );
  return Math.min(points, REVIEW_RECHARGE_FULL);
}

/**
 * Current review recharge (0–{@link REVIEW_RECHARGE_FULL}): the balance stored on the latest
 * live review or introduction. Undone (soft-deleted) reviews drop out, restoring the balance before
 * them exactly.
 */
export async function readReviewRecharge(db: ActorReader): Promise<number> {
  const latest = await readLatestEvent(db, { isNotNull: true });
  return latest === null ? readLegacyReviewRecharge(db) : latest.progressAfter;
}

/** The balance before a review, and the one it leaves: its points added, capped at a full bar. */
export async function readReviewRechargeChange(
  tx: ActorTransaction,
  rating: number,
): Promise<{ after: number; before: number }> {
  const before = await readReviewRecharge(tx);
  return { after: Math.min(REVIEW_RECHARGE_FULL, before + reviewRechargePoints(rating)), before };
}

/**
 * Records a lesson introduction on its meaning card. While reviews are due it drains the whole
 * balance to 0, whatever it was: the charge buys the lesson group the character opens, and that unit is
 * always finished, even at 0. With an empty review queue the bar does not apply and the balance is
 * carried unchanged.
 *
 * `progressBefore` must be read BEFORE the enrollment write: the legacy (pre-stored) balance
 * counts from the latest introduction, which the enrollment itself moves.
 */
export async function recordReviewRechargeIntroduction({
  actorDb,
  progressBefore,
  reviewsDue,
  userCardId,
}: {
  actorDb: ActorDatabaseHandle;
  progressBefore: number;
  reviewsDue: boolean;
  userCardId: string;
}): Promise<void> {
  await actorDb
    .update(actorSchema.userCard)
    .set({
      reviewRechargeAfter: reviewsDue ? 0 : progressBefore,
    })
    .where(eq(actorSchema.userCard.id, userCardId))
    .run();
}

function useReviewRechargeTableChangeVersion() {
  return useTableChangeVersion(
    tableNameMatcher(["user_card", "user_grapheme_meaning_card", "user_card_review"]),
  );
}

export function useGetReviewRechargeQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const withTableChangeVersion = useReviewRechargeTableChangeVersion();
  return () => withTableChangeVersion(["reviewRecharge", { actorDb }] as const);
}

/** Review recharge (0–{@link REVIEW_RECHARGE_FULL}). */
export const [useReviewRechargeQueryOptions] = createUseQueryOptions(
  useGetReviewRechargeQueryKey,
  async ({ queryKey: [_, { actorDb }] }) => readReviewRecharge(actorDb(import.meta.location)),
);
